import type { ApolloClient } from '@apollo/client'

type ObjAny = Record<string, unknown>

export function useAbstractionFetch<Fragment, Query, Variables extends ObjAny>(inp: {
  debug?: string
  fetchPolicy: 'cache-first' | 'cache-only' | 'network-only'
  client: ApolloClient
  query: MyDocumentNode
  fragment: MyDocumentNode
  fragmentName: string
  ids: string[]
  vars: (ids: string[]) => Variables
}): Promise<Fragment[]> {
  if (!inp.ids?.length) return Promise.resolve([])
  const get = (i: string) => useAbstractFindFragment<Fragment>(inp.client, inp.fragment, inp.fragmentName, i)
  const holder = inp.ids.map((id) => ({ id, doc: get(id) }))
  const cached = helpers.arr(holder, (i) => i.doc)
  if (inp.fetchPolicy === 'cache-only') return Promise.resolve(cached)
  const input = inp.fetchPolicy === 'network-only' ? inp.ids : helpers.arr(holder, (i) => (i.doc ? null : i.id))
  if (!input.length) return Promise.resolve(cached)
  return inp.client
    .query<Query, Variables>({ query: TypedDocumentString.getQueryDocument(inp.query), variables: inp.vars(input), fetchPolicy: inp.fetchPolicy })
    .then(() => helpers.arr(inp.ids, (id) => get(id)))
}

type useAbstractionDebounceParams<Fragment, Variables extends ObjAny> = {
  debug?: string
  client: () => ApolloClient
  uniqueName: string
  fetchPolicy?: () => 'cache-first' | 'cache-only' | 'network-only' | None
  checkId: () => None | { id: string | None }
  on: (doc: Fragment) => void
  loading?: (loading: boolean) => void
  query: MyDocumentNode
  fragment: MyDocumentNode
  fragmentName: string
  vars: (ids: string[]) => Variables
}

type useAbstractionDebounceParamsRes = {
  recheck(): void
  refetch(): void
}

export function useAbstractionDebounce<Fragment, Query, Variables extends ObjAny>(inp: useAbstractionDebounceParams<Fragment, Variables>): useAbstractionDebounceParamsRes {
  const client = inp.client()

  const globalTimer = useState(`apollo:debouce:${inp.uniqueName}`, () => ({
    timeout: -1,
    items: [] as { id: string; refetch: boolean; cb: () => void }[],
  }))

  function sync(id: string) {
    const doc = useAbstractFindFragment<Fragment>(client, inp.fragment, inp.fragmentName, id)
    if (inp.debug) console.log(inp.debug, 'sync', id, doc)
    if (doc) inp.on(doc)
    inp.loading?.(false)
  }

  function useAbstractionRun() {
    const copy = [...globalTimer.value.items]
    if (!copy.length) return
    globalTimer.value.items = []
    const refetch = copy.some((i) => i.refetch)

    if (inp.debug) console.log(inp.debug, 'start')
    useAbstractionFetch<Fragment, Query, Variables>({
      ...inp,
      fetchPolicy: refetch ? 'network-only' : inp.fetchPolicy?.() || 'cache-first',
      client,
      ids: copy.map((i) => i.id).filter((i, index, arr) => arr.indexOf(i) === index),
    })
      .then(() => {
        if (inp.debug) console.log(inp.debug, 'then', copy)
        return copy.forEach((i) => {
          if (inp.debug) console.log(inp.debug, 'then cb', i)
          i.cb()
        })
      })
      .catch((err) => {
        if (inp.debug) console.log(inp.debug, 'catch', err)
        throw err
      })
  }

  const initId = inp.checkId()?.id
  if (initId) sync(initId)

  function addToCheck(id: string, refetch: boolean) {
    if (import.meta.server) return
    globalTimer.value.items.push({ id, refetch, cb: () => sync(id) })
    clearTimeout(globalTimer.value.timeout)
    globalTimer.value.timeout = window.setTimeout(() => useAbstractionRun(), 10)
    inp.loading?.(true)
  }

  watch(
    () => inp.checkId()?.id,
    (id) => id && addToCheck(id, false),
    { immediate: true }
  )

  onScopeDispose(() => {
    if (import.meta.server) return
    window.clearTimeout(globalTimer.value.timeout)
    globalTimer.value.timeout = 1
  })

  function refetch() {
    const id = inp.checkId()?.id
    if (id) addToCheck(id, true)
  }

  client.onResetStore(() => Promise.resolve().then(() => refetch()))

  return {
    recheck() {
      const id = inp.checkId()?.id
      if (id) sync(id)
    },
    refetch,
  }
}

// oxlint-disable-next-line explicit-module-boundary-types
export function createUseAbstractionDebounce(client: () => ApolloClient) {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  return function <Fragment, Query, Variables extends ObjAny>(inp: Omit<useAbstractionDebounceParams<Fragment, Variables>, 'client'>) {
    return useAbstractionDebounce<Fragment, Query, Variables>({ client, ...inp })
  }
}
